import { CustomSlideIndex } from '@llx/common';
import { useCallback, useState } from 'react';

import { useCreateNpsScoreMutation } from '@/@generated/codegen/API';
import { useLoggedInUserAuthContext } from '@/components/AuthTokenContextProvider/UserAuthContext';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import { trackEvent, trackPageView } from '../analytic/analytics';
import { useCurrentMeetingDataContext } from '../meeting/context/CurrentMeetingDataContext';

const useMeetingTracking = () => {
  const { userID } = useLoggedInUserAuthContext();
  const {
    currentMeeting,
    hostFields,
    caseMap,
    caseClusterName,
    currentPatientCaseId: currentCaseId,
    currentCaseIndex,
    slideIndex,
    slideName,
    isUserHost,
    isPreview,
  } = useCurrentMeetingDataContext();

  const meetingName = currentMeeting?.name;
  const hostId = currentMeeting?.hostId;

  const [currentMeetingTracked, setCurrentTrackedMeeting] = useState<string | undefined>();
  const [mutationCreateNPSScore] = useCreateNpsScoreMutation();

  // --------------------- methods ------------------------

  const getNumMeetingSlides = () => {
    let numMeetingSlides = 0;
    Object.values(caseMap).forEach((item) => {
      if (item.slides) {
        numMeetingSlides += item.slides.length;
      }
    });
    return numMeetingSlides;
  };
  const getNumMeetingAssets = () => {
    let numMeetingAssets = 0;
    Object.values(caseMap).forEach((item) => {
      if (item.assets) {
        numMeetingAssets += item.assets.length;
      }
    });
    return numMeetingAssets;
  };

  const trackSlideChange = () => {
    if (isPreview) return; // do not track if preview mode
    if (!caseClusterName) return; // do not track if caseCluster is not loaded.

    let pageUrl;
    let pageTitle;

    if (!hostFields.currentCase) {
      pageUrl = `${meetingName}/00_case_selector`;
      pageTitle = `${caseClusterName}/case_selector`;
    } else if (slideIndex === CustomSlideIndex.NPS_SCORE) {
      pageUrl = `${meetingName}/999_NPS_score`;
      pageTitle = `${caseClusterName}/NPS_score`;
      // track caseCluster completed
      if (!isUserHost) {
        trackCaseClusterCompleted();
      }
    } else {
      const customSlideIndex =
        slideIndex === 0 ? '00_patient_profile' : slideIndex?.toString().padStart(2, '0');
      pageUrl = `${meetingName}/${(currentCaseIndex + 1)
        .toString()
        .padStart(2, '0')}_${currentCaseId}/slide_${customSlideIndex}`;
      // pageTitle = `${caseClusterName}/${currentCaseId}/${
      //   slideName || customSlideIndex
      // }`;
      pageTitle = slideName || `slide_${customSlideIndex}`;
    }

    trackPageView(pageUrl);

    // once first trackPageView of a meeting is set, we can actually send a MeetingJoin event
    if (!currentMeetingTracked) {
      if (isUserHost) trackCaseClusterStart();
      else trackMeetingJoin();
      setCurrentTrackedMeeting(meetingName);
    }
  };

  /*
  Depreciated: this is not used anymore but not deleted in the code because maybe we'll use it again ?
  */
  const trackAnswer = useCallback((answerIndex: number, isConfirmed: boolean) => {
    if (isPreview) return; // do not track if preview mode
    trackEvent('Answer', isConfirmed ? 'Submit' : 'Select', `${answerIndex}`);
  }, []);

  const trackMeetingLeave = useCallback(() => {
    if (!currentMeeting) {
      captureSentry({
        title: 'trackMeetingLeave: meeting is undefined',
        detail: {
          meeting: currentMeeting,
          userId: userID,
        },
      });
      return;
    }
    if (!userID) {
      captureSentry({
        title: 'trackMeetingLeave: userId is undefined',
        detail: {
          userId: userID,
          meeting: currentMeeting,
        },
      });
      return;
    }
    // ReactGA.set(getTrackingDetails());
    trackEvent('meeting', 'leave');
    setCurrentTrackedMeeting(undefined);
  }, [currentMeeting?.id, userID]);

  const trackMeetingJoin = useCallback(() => {
    if (isPreview) return; // do not track if preview mode
    trackEvent('meeting', 'join');
  }, []);

  const trackCaseClusterStart = () => {
    if (isPreview) return; // do not track if preview mode
    trackEvent('CaseCluster', 'Start', '', {
      caseIndex: currentCaseIndex + 1,
      meeting_slides: getNumMeetingSlides(),
      meeting_assets: getNumMeetingAssets(),
    });
  };

  const trackCaseClusterStop = useCallback(() => {
    if (isPreview) return; // do not track if preview mode
    trackEvent('CaseCluster', 'Stop');
  }, []);

  const trackCaseClusterCompleted = useCallback(() => {
    if (isPreview) return; // do not track if preview mode
    trackEvent('CaseCluster', 'Completed');
  }, []);

  const createNPSScoreResult = useCallback(
    async (meetingID: string, score: number[]) => {
      if (meetingID !== currentMeeting?.id) {
        captureSentry({
          title: "createNPSScoreResult: meetingID doesn't match",
          detail: {
            meetingID,
            meeting: currentMeeting,
            score,
          },
        });
        return;
      }

      if (isPreview || currentMeeting?.isInTesting) return;

      localStorage.setItem('lastVotedMeetingId', meetingID);
      localStorage.setItem(
        'lastVotedMeetingStartTime',
        currentMeeting?.startDate?.toString() || ''
      );

      try {
        await mutationCreateNPSScore({
          variables: {
            data: {
              meetingId: meetingID,
              scores: score,
            },
          },
        });
      } catch (err) {
        captureSentry({
          title: 'createNPSScoreResult: mutation failed',
          detail: {
            meetingID,
            userID,
            score,
          },
          error: err,
        });
      }
    },
    [currentMeeting]
  );

  // --------------------- return ------------------------

  return {
    trackMeetingLeave,
    trackCaseClusterStop,
    trackAnswer,
    trackSlideChange,
    createNPSScoreResult,
  };
};

export default useMeetingTracking;
